import { Contract, type ContractRunner } from "ethers";
import { VaultAbi } from "../abis";
import type { FeeInfo, PortfolioSummary } from "../types";

export class VaultService {
  contract(vaultAddress: string, runner: ContractRunner) {
    return new Contract(vaultAddress, VaultAbi, runner) as any;
  }

  async assetHasInitialDeposit(
    vaultAddress: string,
    asset: string,
    runner: ContractRunner,
  ): Promise<boolean> {
    return this.contract(vaultAddress, runner).assetHasInitialDeposit(asset);
  }

  async initialDeposit(
    vaultAddress: string,
    asset: string,
    bestVault: string,
    amount: bigint,
    runner: ContractRunner,
    overrides: Record<string, unknown> = {},
  ) {
    return this.contract(vaultAddress, runner).initialDeposit(asset, bestVault, amount, overrides);
  }

  async userDeposit(
    vaultAddress: string,
    asset: string,
    amount: bigint,
    runner: ContractRunner,
    overrides: Record<string, unknown> = {},
  ) {
    return this.contract(vaultAddress, runner).userDeposit(asset, amount, overrides);
  }

  async withdraw(
    vaultAddress: string,
    asset: string,
    amount: bigint,
    runner: ContractRunner,
    overrides: Record<string, unknown> = {},
  ) {
    return this.contract(vaultAddress, runner).withdraw(asset, amount, overrides);
  }

  async getAssetAvailableVaults(
    vaultAddress: string,
    asset: string,
    runner: ContractRunner,
  ): Promise<string[]> {
    return this.contract(vaultAddress, runner).getAssetAvailableVaults(asset);
  }

  async getAssetVaultAssets(
    vaultAddress: string,
    asset: string,
    runner: ContractRunner,
  ): Promise<bigint> {
    return this.contract(vaultAddress, runner).getAssetVaultAssets(asset);
  }

  async getPortfolioSummary(
    vaultAddress: string,
    runner: ContractRunner,
  ): Promise<PortfolioSummary> {
    const [assets, deposited, currentValues, profits, activeCount] =
      await this.contract(vaultAddress, runner).getPortfolioSummary();

    return {
      assets,
      deposited,
      currentValues,
      profits,
      activeCount: Number(activeCount),
    };
  }

  async getAssetProfit(
    vaultAddress: string,
    asset: string,
    runner: ContractRunner,
  ): Promise<bigint> {
    return this.contract(vaultAddress, runner).getAssetProfit(asset);
  }

  async getAssetProfitPercentage(
    vaultAddress: string,
    asset: string,
    runner: ContractRunner,
  ): Promise<bigint> {
    return this.contract(vaultAddress, runner).getAssetProfitPercentage(asset);
  }

  async getTokenBalance(
    vaultAddress: string,
    token: string,
    runner: ContractRunner,
  ): Promise<bigint> {
    return this.contract(vaultAddress, runner).getTokenBalance(token);
  }

  async getAllowedAssets(
    vaultAddress: string,
    runner: ContractRunner,
  ): Promise<string[]> {
    return this.contract(vaultAddress, runner).getAllowedAssets();
  }

  async getFeeInfo(
    vaultAddress: string,
    runner: ContractRunner,
  ): Promise<FeeInfo> {
    const [revenueAddress, feePercentage, rebalanceFeePercentage, merklClaimFeePercentage] =
      await this.contract(vaultAddress, runner).getFeeInfo();
    return {
      revenueAddress,
      feePercentage,
      rebalanceFeePercentage,
      merklClaimFeePercentage,
    };
  }
}
